import PropTypes from 'prop-types';
import InteractionSource from 'apps/interactions/sources/interaction-source';
import CustomFieldsSource from 'apps/interactions/sources/custom-fields-source';
import useInteractionFormFluxStore from 'apps/interactions/form/hooks/use-interaction-form-flux-store';
import { useFeatureFlag } from 'entities/helpers/use-feature-flag';
import { InteractionFormController } from 'apps/interactions/form/interaction-form-controller';
import { createContext, useContext, useEffect, useState } from 'react';

const InteractionFormContext = createContext(() => {});

const InteractionFormProvider = ({ editing = false, children }) => {
  // LaunchDarkly Feature Flags
  const { flagEnabled: hasDxo } = useFeatureFlag('dxo');
  const { flagEnabled: enableInteractionSolicitorType } = useFeatureFlag('enableInteractionSolicitorType');
  const { flagEnabled: interactionDescriptionRichTextEditor } = useFeatureFlag('interactionDescriptionRichTextEditor');

  // Flux Store Data
  const fluxStoreState = useInteractionFormFluxStore(hasDxo);

  // Fetch Labels, Custom Fields, and Interaction Types when the form is mounted
  // (Note: since this data is cached, this only happens the first time a form is mounted. Each subsequent form will use the cached data)
  useEffect(() => {
    if (!fluxStoreState.hasFetchedLabels) InteractionSource.fetchLabels();
    CustomFieldsSource.fetchIfNotCached();
    InteractionSource.fetchTypesUnlessCached();
  }, [fluxStoreState.hasFetchedLabels]);

  // Conditional Field Rules
  const [rules, setRules] = useState([]);

  // Fetch rules for conditional fields
  // In an ideal world, we STOP using all of these configurations from DNA settings and simply define them as rules from the DB directly.
  // For the moment, though, we must map the DNA settings into rules.
  useEffect(() => {
    const getAndSetRules = async () => {
      try {
        const interactionStandardFieldsConfig = fluxStoreState.interactionStandardFieldsConfig || {};
        const isSummaryRequired = interactionStandardFieldsConfig.summary?.required || false;
        const hasCustomFields = fluxStoreState.hasCustomFields || false;
        const orgCustomFields = fluxStoreState.orgCustomFields || [];
        const fieldTriggerRules = (await InteractionFormController.fetchRules()) || [];

        const dnaMappedRules =
          InteractionFormController.mapDnaToFieldTriggerRules(interactionStandardFieldsConfig, isSummaryRequired) || [];

        const customFieldRules =
          InteractionFormController.mapCustomFieldsToRules(hasCustomFields, orgCustomFields) || [];

        const combinedRules = [...customFieldRules, ...fieldTriggerRules, ...dnaMappedRules];
        setRules(combinedRules);
      } catch (error) {
        console.error('Error fetching rules:', error);
        setRules([]); // Set to empty array on error to avoid breaking the form
      }
    };

    getAndSetRules();
  }, [
    fluxStoreState.isSummaryRequired,
    fluxStoreState.interactionStandardFieldsConfig,
    fluxStoreState.hasCustomFields,
    fluxStoreState.orgCustomFields,
  ]);

  return (
    <InteractionFormContext.Provider
      value={{
        rules,
        editing,
        ...fluxStoreState,
        enableInteractionSolicitorType,
        interactionDescriptionRichTextEditor,
      }}
    >
      {children}
    </InteractionFormContext.Provider>
  );
};

InteractionFormProvider.propTypes = {
  editing: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

const useInteractionFormContext = () => {
  const context = useContext(InteractionFormContext);
  if (!context) {
    throw new Error('useInteractionFormContext must be used within an InteractionFormProvider');
  }
  return context;
};

export { InteractionFormContext, InteractionFormProvider, useInteractionFormContext };
